import { From } from './From'
import { To } from './To'
import { Form } from '@/ui/atoms/form/Form'
import { SwapMode, UseBridgeResults } from '@/features/bridge/logic/useBridge'
import { Button } from '@/ui/atoms/button/Button'
import { useDynamicContext } from '@dynamic-labs/sdk-react-core'
import { isEthereumWallet } from '@dynamic-labs/ethereum'
import * as hl from '@nktkas/hyperliquid'
import { BridgeObjective } from '@/features/actions/flavours/bridge/types'
import { useState } from 'react'
import { isTestnet } from '@/config/consts'
import { trackEvent } from '@/utils/fathom'
import { ToggleButton } from '@/features/swap/components/forms/swap-form/ToggleButton'
import { normalizeUnsupportedCharacters } from '@/utils/strings'

const transport = new hl.HttpTransport({ isTestnet })

export function BridgeForm(props: UseBridgeResults & { setErrorMessage: (s: string) => void }) {
  const {
    form,
    guestMode,
    fromTokenField,
    toTokenField, // Commented out as it's unused
    openConnectModal,
    actions,
    pageStatus,
    swapDirection,
    swapMode,
    setErrorMessage,
  } = props

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(() => {})}>
        <input type="hidden" name="swapMode" value={swapMode} />
        <div className="relative z-10">
          <From
            control={form.control}
            assets={fromTokenField.availableAssets}
            selectedAsset={fromTokenField.selectedAsset}
            changeToken={fromTokenField.changeToken}
            maxSelectedFieldName={fromTokenField.maxSelectedFieldName}
            onChange={fromTokenField.onChange}
            onSetMax={fromTokenField.onSetMax}
            maxValue={fromTokenField.maxValue}
            swapMode={swapMode}
          />
          <ToggleButton
            className="absolute right-1/2 bottom-0 translate-x-1/2 translate-y-3/4"
            onClick={swapDirection}
          />
        </div>
        <To
          control={form.control}
          assets={toTokenField.availableAssets}
          selectedAsset={toTokenField.selectedAsset}
          changeToken={toTokenField.changeToken}
          maxSelectedFieldName={toTokenField.maxSelectedFieldName}
          onChange={toTokenField.onChange}
          swapMode={swapMode}
        />

        <div className="mt-4">
          <div className="mb-2 text-sm text-white/60" />
          {guestMode ? (
            <Button className="w-full rounded-lg" onClick={openConnectModal}>
              Connect wallet
            </Button>
          ) : (
            <BridgeButton
              objectives={actions}
              onFinish={pageStatus.goToSuccessScreen}
              enabled={pageStatus.actionsEnabled}
              swapMode={swapMode}
              setErrorMessage={setErrorMessage}
            />
          )}
        </div>
      </form>
    </Form>
  )
}

function BridgeButton(props: {
  objectives: BridgeObjective[]
  onFinish: () => void
  enabled: boolean
  swapMode: SwapMode
  setErrorMessage: (s: string) => void
}) {
  const { primaryWallet } = useDynamicContext()
  const [isLoading, setIsLoading] = useState(false)
  const { setErrorMessage } = props

  async function handleClick() {
    setErrorMessage('')

    if (!primaryWallet || !isEthereumWallet(primaryWallet) || !props.objectives[0]) {
      return
    }

    const objective = props.objectives[0]
    const fromSymbol = objective.from.token.symbol

    // Get tokenId from the objective or fallback to the mapping
    const tokenId = objective.from.tokenId

    if (!tokenId) {
      setErrorMessage(`Token ID not found for: ${fromSymbol}`)
      return
    }

    setIsLoading(true)

    try {
      const walletClient = await primaryWallet.getWalletClient()

      if (props.swapMode === 'bridgeToHyperEVM') {
        // Use HyperLiquid client for bridging to HyperEVM
        const hlClient = new hl.WalletClient({
          wallet: walletClient,
          transport,
          isTestnet,
        })

        const tokenString = `${String(normalizeUnsupportedCharacters(fromSymbol, { uppercase: true }))}:${tokenId}`

        const data = await hlClient.spotSend({
          destination: objective.from.systemAddress as `0x${string}`,
          amount: objective.amount.toString(),
          token: tokenString as `${string}:0x${string}`,
        })

        if (data.status === 'ok') {
          trackEvent(`bridge_success_${fromSymbol}_${props.swapMode}`)
          props.onFinish()
        } else {
          throw new Error(`Transfer failed: ${data.status}`)
        }
      } else {
        const tokenAddress = objective.from.token.address as `0x${string}`
        const publicClient = await primaryWallet.getPublicClient()

        // For bridging to HyperCore, use wallet sendBalance
        const token =
          objective.from.token.symbol === 'HYPE'
            ? undefined
            : {
                address: tokenAddress,
                decimals: objective.from.token.decimals,
              }
        const tx = await primaryWallet.sendBalance({
          toAddress: objective.from.systemAddress as `0x${string}`,
          token,
          amount: objective.amount.toString(),
        })

        await publicClient.waitForTransactionReceipt({ hash: tx })

        trackEvent(`bridge_success_${fromSymbol}_${props.swapMode}`)

        props.onFinish()
      }
    } catch (error) {
      trackEvent(`bridge_error_${fromSymbol}_${props.swapMode}`)
      console.error(error)
      setErrorMessage(
        error instanceof Error
          ? error.message.includes('User rejected')
            ? 'User rejected the request'
            : error.message
          : 'Transaction Failed',
      )
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="relative pb-1">
      <Button className="w-full rounded-lg" disabled={!props.enabled || isLoading} onClick={handleClick}>
        {isLoading
          ? 'Transferring...'
          : `Transfer ${props.objectives[0]?.from.token.symbol || 'Token'} to ${props.swapMode === 'bridgeToHyperEVM' ? 'HyperEVM' : 'HyperCore'}`}
      </Button>
    </div>
  )
}
